/**
 * El año de cobros del consultorio.
 *
 * Reemplaza a la tira plana de meses que había antes. El dueño (8-set-2026)
 * pidió dos cosas: que se vea **de un golpe qué necesita acción y qué no**, y
 * que estén **los doce meses del año** aunque solo algunos le toquen — «los
 * médicos están acostumbrados a razonar sobre los doce meses».
 *
 * ── Lo que la grilla NO puede hacer ────────────────────────────────────────
 *
 * Una celda ajena no es cliqueable y no trae con qué serlo: el servidor no
 * manda el id de un cobro que no es de quien mira. Eso no es una decisión de
 * esta pantalla —está en `domain/anio-cobros.ts`— pero conviene saberlo acá
 * antes de intentar hacerla cliqueable «porque se ve raro que unas sí y otras
 * no».
 *
 * ── «Sin definir» es el estado principal, no el raro ───────────────────────
 *
 * Medido el 8-set-2026: de los 369 cargos emitidos, 250 vienen de repartos sin
 * confirmar y 10 no tienen ningún pagador. O sea que **dos tercios de la
 * grilla** van a estar en «sin definir» mientras dure la conciliación, y va a
 * ir bajando sola a medida que se cierren consultorios. Por eso ese estado se
 * diseñó como un ciudadano de primera —tiene su color, su símbolo y su
 * explicación— y no como un hueco vacío que parece un error.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { periodLabelShort } from '@cleanblue/shared';
import { useApi } from '@/lib/hooks';
import { haptic } from '@/lib/haptics';
import { Async, Empty } from '@/components/ui';
import { Icon } from '@/components/common/NavIcons';

type Estado = 'no-aplica' | 'sin-generar' | 'sin-definir' | 'mio' | 'compartido' | 'ajeno';

interface Mes {
  period: string;
  estado: Estado;
  charge_payer_id?: string;
  pagado?: boolean;
  responsable?: string;
  entre?: number;
}

interface Unidad {
  unit_id: string; unit_code: string; unit_name: string | null; months: Mes[];
}

/** El mes en tres letras: en una celda de 80 px no entra «setiembre». */
const MES_CORTO = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'set', 'oct', 'nov', 'dic'];

/** ¿Este mes pide algo de la persona que mira? Es lo que separa las dos
 *  secciones, y lo único que hace falta saber para el resumen de arriba. */
function meToca(m: Mes): boolean {
  return (m.estado === 'mio' || m.estado === 'compartido') && !m.pagado;
}

function claseDe(m: Mes): string {
  if (m.estado === 'no-aplica') return 'mes mes--fuera';
  if (m.estado === 'sin-definir' || m.estado === 'sin-generar') return 'mes mes--sindefinir';
  if (m.estado === 'ajeno') return `mes mes--ajeno${m.pagado ? '' : ' mes--ajeno-debe'}`;
  return m.pagado ? 'mes mes--pagado' : 'mes mes--debo';
}

/** Lo que lee un lector de pantalla, y el `title` del escritorio. La grilla no
 *  puede depender solo del color: el estado se dice con palabras. */
function descripcion(m: Mes): string {
  const cuando = periodLabelShort(m.period);
  switch (m.estado) {
    case 'no-aplica': return `${cuando}: el consultorio todavía no era cliente`;
    case 'sin-generar':
    case 'sin-definir': return `${cuando}: todavía no está definido quién responde por este mes`;
    case 'ajeno': return `${cuando}: le toca a ${m.responsable}, ${m.pagado ? 'ya pagó' : 'sin pagar'}`;
    case 'compartido': return `${cuando}: lo pagan entre ${m.entre}, ${m.pagado ? 'tu parte está pagada' : 'te toca tu parte'}`;
    default: return `${cuando}: ${m.pagado ? 'pagado' : 'te toca pagarlo'}`;
  }
}

function Celda({ m }: { m: Mes }) {
  const i = Number(m.period.slice(5, 7)) - 1;
  const cuerpo = (
    <>
      <span className="mes__nombre">{MES_CORTO[i]}</span>
      <span className="mes__marca" aria-hidden>
        {m.estado === 'no-aplica' ? '·'
          : m.estado === 'sin-definir' || m.estado === 'sin-generar' ? '?'
            : m.pagado ? <Icon name="confirmar" size={14} />
              : m.estado === 'ajeno' ? <Icon name="doctor" size={14} />
                : <Icon name="cobros" size={14} />}
      </span>
      {/* Solo el primer nombre: en 80 px no entra «José David Brenes», y de
          quién es el mes se entiende igual entre compañeros de consultorio. */}
      {m.estado === 'ajeno' && (
        <span className="mes__pie">{m.responsable?.split(' ')[0]}</span>
      )}
      {m.estado === 'compartido' && <span className="mes__pie">entre {m.entre}</span>}
    </>
  );

  // Solo lo propio se abre: de lo ajeno no llega ni el id (ver el encabezado).
  if (m.charge_payer_id) {
    return (
      <Link
        to={`/portal/cobros/${m.charge_payer_id}`}
        className={claseDe(m)}
        title={descripcion(m)}
        aria-label={descripcion(m)}
        onClick={() => haptic.light()} // HAPTIC_FEEDBACK
      >
        {cuerpo}
      </Link>
    );
  }
  return <div className={claseDe(m)} title={descripcion(m)} aria-label={descripcion(m)} role="img">{cuerpo}</div>;
}

export function AnioCobros() {
  const [anio, setAnio] = useState<number | null>(null);
  const query = useApi<{ year: number; years: number[]; units: Unidad[] }>(
    `/portal/billing-year${anio ? `?year=${anio}` : ''}`,
  );

  return (
    <Async query={query} empty={(d) => d.units.length === 0 && (
      <Empty icon="cobros" title="Todavía no tenés cobros" />
    )}>
      {(data) => {
        const pendientes = data.units.flatMap((u) => u.months.filter(meToca));
        return (
          <>
            {/* Lo primero, y en una sola frase: qué necesita acción. Antes había
                que leer una tira de meses todos iguales para deducirlo. */}
            <div className={`anio-resumen${pendientes.length > 0 ? ' anio-resumen--debe' : ''}`}>
              {pendientes.length === 0
                ? 'Estás al día. No hay meses tuyos sin pagar.'
                : `Te toca pagar ${pendientes.length} ${pendientes.length === 1 ? 'mes' : 'meses'}.`}
            </div>

            {data.years.length > 1 && (
              <div
                className="segmented mt-2"
                role="group"
                aria-label="Año"
                style={{
                  '--seg-n': data.years.length,
                  '--seg-i': data.years.indexOf(data.year),
                } as React.CSSProperties}
              >
                <div className="segmented__pill" />
                {data.years.map((y) => (
                  <button
                    key={y}
                    type="button"
                    aria-pressed={y === data.year}
                    className={`segmented__opt${y === data.year ? ' is-active' : ''}`}
                    onClick={() => { if (y !== data.year) haptic.light(); setAnio(y); }} // HAPTIC_FEEDBACK
                  >
                    {y}
                  </button>
                ))}
              </div>
            )}

            {data.units.map((u) => (
              <section key={u.unit_id} className="msec">
                {data.units.length > 1 && (
                  <div className="msec__title">{u.unit_code}{u.unit_name ? ` · ${u.unit_name}` : ''}</div>
                )}
                <div className="anio-grilla">
                  {u.months.map((m) => <Celda key={m.period} m={m} />)}
                </div>
              </section>
            ))}

            <div className="anio-leyenda">
              <span><i className="mes mes--debo" /> Te toca</span>
              <span><i className="mes mes--pagado" /> Pagado</span>
              <span><i className="mes mes--ajeno" /> De otro médico</span>
              <span><i className="mes mes--sindefinir" /> Sin definir</span>
            </div>
            <p className="small muted mt-1">
              «Sin definir» es un mes que todavía no tiene responsable asignado. Lo estamos
              ordenando consultorio por consultorio.
            </p>
          </>
        );
      }}
    </Async>
  );
}
